import {
  AutocompleteProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@freighthub/silhouette-core'
import cn from 'classnames'
import { useObserver } from 'mobx-react-lite'
import { FC, ReactElement, useEffect, useState } from 'react'
import { useI18n } from 'utils/localisation'

import { ContactsActionList } from '../Contacts/ContactsActionList'
import { IContactJSON } from '../Contacts/ContactsModel'
import { AutocompleteResult } from '../NetworkAutocomplete/NetworkAutocompleteOption'
import { IOrganizationJSON } from '../Organizations/OrganizationsModel'
import { PlaceAutocomplete } from '../PlaceAutocomplete/PlaceAutocomplete'
import { PlacesSearchType } from '../PlaceAutocomplete/PlaceService'
import { ILocationModelJSON, TLocation } from './LocationsModel'

const useStyles = makeStyles<Theme>((theme) => ({
  dialogContent: {
    minHeight: 485,
    overflowY: 'initial',
  },
  autocompleteWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  autocompleteLabel: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  autocomplete: {
    flex: 4,
  },
  nameField: {
    marginBottom: theme.spacing(4),
  },
  locationAutocomplete: {
    height: theme.spacing(8),
    paddingBottom: theme.spacing(4),
  },
}))

export interface LocationDialogProps {
  onClickSave: (location: TLocation) => void
  onClickCancel: () => void
  setOrganization?: (organization: IOrganizationJSON) => void
  removeContact?: (contactId: string) => void
  setLocation?: (location: ILocationModelJSON) => void
  open: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  searchLocation: (typesArray: PlacesSearchType[], searchText: string, filter) => any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  detailsLocation: (placeId: string) => Promise<any>
  location: TLocation
  organization?: Partial<IOrganizationJSON>
  renderContactsAutocomplete: (props: {
    contactIds: string[]
    handleContactSelection: (selection: AutocompleteResult) => void
    filter: AutocompleteProps['filterOptions']
    fetchDefaultOptionOnOpen: boolean
  }) => ReactElement
  renderOrganizationsAutocomplete: (props: {
    handleOrganizationSelection: (selection: AutocompleteResult) => void
  }) => ReactElement
  getContacts: (_ids: string[]) => Promise<IContactJSON[]>
  isEditing?: boolean
}

export const LocationDialog: FC<LocationDialogProps> = ({
  location,
  getContacts,
  setOrganization,
  removeContact,
  onClickSave,
  setLocation,
  isEditing,
  organization,
  onClickCancel,
  open,
  renderOrganizationsAutocomplete,
  searchLocation,
  detailsLocation,
  renderContactsAutocomplete,
}) => {
  const [contacts, setContacts] = useState<IContactJSON[]>([])
  const { triedSubmit, organizationId } = location
  const { translate } = useI18n()
  const classes = useStyles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const isOrganizationInvalid = triedSubmit && !isEditing && !organizationId.value
  const contactIds = contacts.map((contact) => contact._id)

  useEffect(() => {
    if (location.contactIds) {
      fetchContacts()
    }
    if (location.place.value?.companyName) {
      location.setCompanyName(location.place.value.companyName)
    }
  }, [])

  const fetchContacts = () => {
    getContacts(location.contactIds).then(setContacts)
  }

  const handleContactSelection = (selection: AutocompleteResult) => {
    location.setContactId(selection.value.toString())
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    setContacts([...contacts, selection.data.entity])
  }

  const handleOrganizationSelection = (selection: AutocompleteResult) => {
    location.setOrganizationId(selection.value.toString())
    if (setOrganization) {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      setOrganization(selection.data.entity)
    }
  }

  const handleRemoveContact = (_id: string) => {
    location.removeContact(_id)
    // @ts-expect-error TS(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    removeContact(_id)
    const newContacts = contacts.filter((contact) => contact._id !== _id)
    setContacts(newContacts)
  }

  const filterContacts = (_: unknown[], { inputValue }): IContactJSON[] => {
    if (!contacts) {
      return []
    }
    return contacts.filter((contact) => contact._id === inputValue)
  }

  const handleSave = () => {
    location.submit()
    if (location.isValid) {
      onClickSave(location)
      if (setLocation) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
        setLocation(null)
      }
    }
  }

  const confirmButtonLabel = location._id ? translate('directory-button_update') : translate('directory-button_save')
  const title = location._id ? 'directory-location_title_edit' : 'directory-location_title_create'
  return useObserver(() => (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      onClose={onClickCancel}
      maxWidth="sm"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      PaperProps={{ 'data-userlane-id': 'directory-modal-location', 'data-testid': 'location-dialog' } as any}
    >
      <DialogTitle onClose={onClickCancel} showClose>
        {translate(title)}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography
          component="div"
          color={isOrganizationInvalid ? 'error' : 'textPrimary'}
          className={classes.autocompleteWrapper}
        >
          <Typography className={classes.autocompleteLabel}>{translate('directory-organization_title')}</Typography>
          <div className={classes.autocomplete}>
            {!isEditing &&
              renderOrganizationsAutocomplete({
                handleOrganizationSelection,
              })}
            {isEditing && organization && <span>{organization.name}</span>}
          </div>
        </Typography>
        <TextField
          className={classes.nameField}
          fullWidth
          defaultValue={location.name.value.toString()}
          label={translate('directory-location_name')}
          onChange={(e) => location.setName(e.target.value)}
          helperText={location.validateName().isInvalid && translate(location.validateName().message)}
          error={location.validateName().isInvalid}
          margin="normal"
          required
        />
        <div className={cn(classes.autocompleteWrapper, classes.locationAutocomplete)}>
          <Typography color={location.place.isInvalid ? 'error' : 'textPrimary'} className={classes.autocompleteLabel}>
            {translate('directory-location_title')}
          </Typography>
          <div className={classes.autocomplete}>
            <PlaceAutocomplete
              onSelect={location.setPlace}
              label={translate('directory-location_autocomplete_placeholder')}
              types={['address']}
              filter="pol"
              name="origin"
              value={location.place.value?.locationWithoutCompanyName}
              error={null}
              search={searchLocation}
              details={detailsLocation}
            />
          </div>
        </div>
        <div className={classes.autocompleteWrapper}>
          <Typography className={classes.autocompleteLabel}>{translate('directory-contact_title')}</Typography>
          <div className={classes.autocomplete}>
            {renderContactsAutocomplete({
              contactIds,
              handleContactSelection,
              filter: filterContacts,
              fetchDefaultOptionOnOpen: true,
            })}
          </div>
        </div>
        <ContactsActionList contacts={contacts} removeContact={handleRemoveContact} />
        <TextField
          fullWidth
          defaultValue={location.notes.value.toString()}
          label={translate('directory-form_notes')}
          onChange={(e) => location.setNotes(e.target.value)}
          helperText={location.validateNotes().isInvalid && translate(location.validateNotes().message)}
          error={location.validateNotes().isInvalid}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClickCancel}>{translate('directory-button_cancel')}</Button>
        <Button onClick={handleSave} variant="contained" data-userlane-id="directory-modal-location-save">
          {confirmButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  ))
}
